import { ClockRewind } from "@untitledui/icons";
import type { PartnerLegacySummary } from "@mfa-aff/shared";
import { HelpTip, JalaliDate, KeyValueList, Money, SectionCard, formatNumber } from "@mfa-aff/ui";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";

/**
 * «سابقهٔ شما در سیستم قبلی»: what came over from the old WHMCS affiliate system (join date,
 * commissions recorded there, old visits with a small monthly chart, old commission-to-wallet
 * transfers). Deliberately no "earned − paid − balance" equation: some old commissions were
 * cancelled or adjusted in the old system, so those numbers do not add up row by row.
 */
export const LegacyCard = ({ legacy }: { legacy: PartnerLegacySummary }) => {
    const max = Math.max(1, ...legacy.visitsByMonth.map((m) => m.clicks));
    return (
        <SectionCard
            icon={ClockRewind}
            iconColor="gray"
            title="سابقهٔ شما در سیستم قبلی"
            description="اطلاعاتی که از سیستم قبلی بازاریابی میزبان‌فا به این پنل آمده است؛ فقط برای مشاهده."
            className="h-full"
        >
            <div className="flex flex-col gap-5">
                <KeyValueList
                    items={[
                        ...(legacy.joinedAt ? [{ label: "عضو از", value: <JalaliDate value={legacy.joinedAt} /> }] : []),
                        {
                            label: (
                                <span className="flex items-center gap-1.5">
                                    پورسانت ثبت‌شده در سیستم قبلی
                                    <HelpTip
                                        title="پورسانت ثبت‌شده در سیستم قبلی"
                                        description="جمع همهٔ پورسانت‌هایی که سیستم قبلی برایتان ثبت کرده بود. بعضی از آن‌ها بعداً همان‌جا لغو یا اصلاح شده‌اند (مثلاً با برگشت پول مشتری)، برای همین این عدد لزوماً با جمع «پرداخت‌شده» و موجودی منتقل‌شده برابر نیست. موجودی شما همان مبلغی است که سیستم قبلی در روز انتقال نشان می‌داد."
                                    />
                                </span>
                            ),
                            hint: legacy.commissionRows > 0 ? `${formatNumber(legacy.commissionRows)} ردیف` : undefined,
                            value: <Money rial={legacy.commissionRial} size="sm" />,
                        },
                        ...(legacy.payoutCount > 0
                            ? [{ label: "انتقال به کیف پول در سیستم قبلی", hint: `${formatNumber(legacy.payoutCount)} بار`, value: <Money rial={legacy.payoutRial} size="sm" /> }]
                            : []),
                        ...(legacy.visitors !== null ? [{ label: "بازدیدهای قبلی", value: `${formatNumber(legacy.visitors)} بازدید` }] : []),
                    ]}
                />
                {legacy.visitsByMonth.length > 1 && (
                    <figure className="flex flex-col gap-2">
                        <figcaption className="text-xs text-tertiary">بازدید لینک‌های شما در ماه‌های آخر سیستم قبلی</figcaption>
                        <div className="flex h-20 items-end gap-1" dir="ltr" role="img" aria-label="نمودار بازدیدهای ماهانه در سیستم قبلی">
                            {legacy.visitsByMonth.map((m) => (
                                <div
                                    key={m.month}
                                    title={`${m.label}: ${formatNumber(m.clicks)} بازدید`}
                                    className="min-w-0 flex-1 rounded-t bg-utility-brand-300"
                                    style={{ height: `${Math.max(4, Math.round((m.clicks / max) * 100))}%` }}
                                />
                            ))}
                        </div>
                        <div className="flex justify-between text-xs text-quaternary" dir="ltr">
                            <span dir="rtl">{legacy.visitsByMonth[0]!.label}</span>
                            <span dir="rtl">{legacy.visitsByMonth[legacy.visitsByMonth.length - 1]!.label}</span>
                        </div>
                    </figure>
                )}
                <div className="flex flex-wrap gap-2">
                    <Button color="secondary" size="sm" href="/orders">
                        سابقهٔ پورسانت
                    </Button>
                    {legacy.payoutCount > 0 && (
                        <Button color="secondary" size="sm" href="/wallet">
                            سابقهٔ برداشت
                        </Button>
                    )}
                </div>
            </div>
        </SectionCard>
    );
};
